import Box from '@mui/material/Box';
import Chip, { type ChipProps } from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { isTransientStatus } from '../_lib/captureEligibility';

/**
 * AUT-219 — the payment status, painted so the operator can read the money
 * state at a glance.
 *
 * Colour carries meaning here, not decoration:
 *   - `authorized` — info. The money is blocked but NOT ours; nothing is done.
 *   - `capturing`  — warning + a live spinner. A CLAIM state: the settle went
 *     out and the answer never came back. It must not look like a status the
 *     row is allowed to rest in, because a row stuck here is a queued
 *     reconciliation, and the sub-line says so.
 *   - `captured` / `released` — success. The money moved.
 *   - `voided` — neutral. The hold was dropped; no money ever moved, which is
 *     a different thing from a refund and is coloured differently on purpose.
 *   - `refunded` / `partially_refunded` — warning. Money went back.
 *   - `failed` — error.
 *
 * Unknown / future statuses fall through to a default chip with the raw enum
 * as its label (i18n `defaultValue`) rather than rendering blank.
 */
const STATUS_COLOR: Record<string, ChipProps['color']> = {
  pending: 'default',
  processing: 'default',
  succeeded: 'success',
  authorized: 'info',
  capturing: 'warning',
  captured: 'success',
  voided: 'default',
  released: 'success',
  refunded: 'warning',
  partially_refunded: 'warning',
  failed: 'error',
};

export const PaymentStatusChip = ({ status }: { status: string }): ReactElement => {
  const { t } = useTranslation();
  const transient = isTransientStatus(status);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.4 }}>
      <Chip
        size="small"
        color={STATUS_COLOR[status] ?? 'default'}
        variant={transient ? 'outlined' : 'filled'}
        data-testid="payment-status-chip"
        data-status={status}
        data-transient={transient ? 'true' : 'false'}
        icon={transient ? <CircularProgress size={10} color="inherit" thickness={6} /> : undefined}
        label={t(`orders.detail.payment.statusValue.${status}`, { defaultValue: status })}
      />
      {transient ? (
        <Typography variant="caption" color="warning.main" sx={{ fontSize: 11, lineHeight: 1.3 }}>
          {t('orders.detail.payment.capturingHint')}
        </Typography>
      ) : null}
    </Box>
  );
};
